// src/components/sections/Portfolio.tsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useInView } from 'react-intersection-observer'; // Add this import

// Import des images
// @ts-ignore
import lescarabistouilles from '../../images/sites/Logo_transparent.png';
// @ts-ignore
import ftconseils from '../../images/sites/4tconseils-detoure.png';
// @ts-ignore
// import portfolio1 from '../../images/sites/Logo_transparent.png';
// @ts-ignore
// import portfolio2 from '../../images/sites/Logo_transparent.png';

import './Portfolio.css';


interface Project {
  id: number;
  titleKey: string;
  descriptionKey: string;
  image: string;
  color: string;
  tags: string[];
  url: string;  // Ajout de l'URL
}

const Portfolio: React.FC = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [activeProject, setActiveProject] = useState(0);

  const projects: Project[] = [

    {
      id: 1,
      titleKey: 'portfolio.projects.corporate.title',
      descriptionKey: 'portfolio.projects.corporate.description',
      image: lescarabistouilles,
      color:'#fa709a',
      tags: ['React', 'Node.js', 'TypeScript', 'Tailwind'],
      url: 'https://lescarabistouilles.com'
    },
    {
      id: 2,
      titleKey: 'portfolio.projects.portfolio.title',
      descriptionKey: 'portfolio.projects.portfolio.description',
      image: ftconseils,
      color: '#4a90e2',
      tags: ['React', 'Node.js', 'TypeScript'],
      url: 'https://4tconseils.com'
    }
  ];

  const nextProject = () => {
    setActiveProject((prev) => (prev + 1) % projects.length);
  };

  const prevProject = () => {
    setActiveProject((prev) => (prev - 1 + projects.length) % projects.length);
  };

  return (
    <section id="portfolio" className="portfolio" ref={ref}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">{t('portfolio.title')}</h2>
          <p className="section-subtitle">{t('portfolio.subtitle')}</p>
        </motion.div>

        <motion.div 
          className="portfolio-showcase"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <button 
            className="carousel-button carousel-prev"
            onClick={prevProject}
            aria-label="Previous project"
          >
            <ChevronLeft size={24} />
          </button>

          <div className="portfolio-content">
            <motion.div 
              className="portfolio-preview"
              key={activeProject}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
            >
              <div 
                className="preview-screen"
                style={{ 
                  background: `linear-gradient(135deg, ${projects[activeProject].color} 0%, ${projects[activeProject].color}aa 100%)` 
                }}
              >
                <div className="preview-header">
                  <div className="preview-controls">
                    <span className="control"></span>
                    <span className="control"></span>
                    <span className="control"></span>
                  </div>
                  <div className="preview-url">
                    <span>{projects[activeProject].url}</span>
                  </div>
                </div>
                <div className="preview-content">
                  <img 
                    src={projects[activeProject].image} 
                    alt={t(projects[activeProject].titleKey)}
                    className="project-image"
                  />
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="portfolio-details"
              key={`details-${activeProject}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h3 className="project-title">
                {t(projects[activeProject].titleKey)}
              </h3>
              <p className="project-description">
                {t(projects[activeProject].descriptionKey)}
              </p>
              <div className="project-tags">
                {projects[activeProject].tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
              <button className="btn btn-primary project-link" onClick={() => window.open(projects[activeProject].url, '_blank')}>
                <span>{t('portfolio.view_project')}</span>
                <ExternalLink size={16} />
              </button>
            </motion.div>
          </div>

          <button 
            className="carousel-button carousel-next"
            onClick={nextProject}
            aria-label="Next project"
          >
            <ChevronRight size={24} />
          </button>
        </motion.div>

        <div className="portfolio-indicators">
          {projects.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === activeProject ? 'active' : ''}`}
              onClick={() => setActiveProject(index)}
              aria-label={`Go to project ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;